import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { AdKitKey } from "types/enums";
import type { PlacementCatalogEntry } from "../../placementCatalog";
import { PlacementScreenThumb } from "../PlacementScreenThumb";
import {
  isPlacementReady,
  placementStatus,
  type CampaignDraft,
  type HoleLoadTiming,
} from "./campaignDraft";

/** Placements drawn on the ground, where a transparent PNG reads as paint. */
const GROUND_OVERLAYS = new Set<AdKitKey>([
  AdKitKey.ROUND_TEE_OVERLAY,
  AdKitKey.RANGE_TEE_OVERLAY,
  AdKitKey.RANGE_FAIRWAY_OVERLAY,
  AdKitKey.SKILL_EVENT_TEE_OVERLAY,
]);

export function SetupSection({
  number,
  title,
  children,
}: {
  number?: number;
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 border-t border-border pt-5">
      <h3 className="flex items-center text-base font-semibold text-ink">
        {number !== undefined && (
          <span className="mr-2 inline-flex size-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
            {number}
          </span>
        )}
        {title}
      </h3>
      {children}
    </section>
  );
}

function TimingField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-xs text-text-muted">
        {label}
      </label>
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        min={0}
        step={0.1}
        value={value}
        onChange={(e) => {
          const next = Number(e.target.value);
          if (Number.isFinite(next) && next >= 0) onChange(next);
        }}
        className="w-28 rounded-lg"
      />
    </div>
  );
}

interface ImagesStepProps {
  draft: CampaignDraft;
  /** The selected placements, in setup order. */
  placements: PlacementCatalogEntry[];
  currentIndex: number;
  onSelectPlacement: (index: number) => void;
  timing: HoleLoadTiming;
  onTimingChange: (timing: HoleLoadTiming) => void;
  /** The questions for the current placement (holes, images). */
  children: ReactNode;
}

export function ImagesStep({
  draft,
  placements,
  currentIndex,
  onSelectPlacement,
  timing,
  onTimingChange,
  children,
}: ImagesStepProps) {
  const current = placements[currentIndex];
  if (!current) return null;
  const readyCount = placements.filter((p) =>
    isPlacementReady(draft.setup[p.key], p),
  ).length;
  const allReady = readyCount === placements.length;

  return (
    <>
      <div className="mt-6 rounded-2xl border border-border bg-surface px-4.5 py-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2.5">
          <span className="text-xs font-bold tracking-wide text-text-muted uppercase">
            Placement {currentIndex + 1} of {placements.length}
          </span>
          <span
            className={cn(
              "text-sm font-semibold",
              allReady ? "text-good" : "text-amber-700 dark:text-amber-400",
            )}
          >
            {readyCount} of {placements.length} placements ready
          </span>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {placements.map((placement, i) => {
            const setup = draft.setup[placement.key];
            const ready = isPlacementReady(setup, placement);
            const isCurrent = i === currentIndex;
            return (
              <button
                key={placement.key}
                type="button"
                aria-current={isCurrent ? "step" : undefined}
                onClick={() => onSelectPlacement(i)}
                className={cn(
                  "flex items-center gap-2.5 rounded-xl border-[1.5px] px-3 py-2 text-left transition-colors",
                  isCurrent
                    ? "border-primary bg-surface"
                    : "border-border hover:border-ink/40",
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "flex size-6.5 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                    ready
                      ? "bg-good-soft text-good"
                      : isCurrent
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-text-muted",
                  )}
                >
                  {ready ? <Check className="size-3.5" /> : i + 1}
                </span>
                <span>
                  <span className="block text-sm font-semibold text-ink">
                    {placement.name}
                  </span>
                  <span
                    className={cn(
                      "block text-xs font-semibold",
                      ready
                        ? "text-good"
                        : "text-amber-700 dark:text-amber-400",
                    )}
                  >
                    {placementStatus(setup, placement)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-5 rounded-2xl border border-border bg-surface p-6">
        <div className="flex flex-wrap items-center gap-5.5">
          <PlacementScreenThumb
            zone={current.screenZone}
            className="w-55 rounded-lg"
          />
          <div className="min-w-0 flex-[1_1_300px]">
            <h2 className="poppins text-xl font-semibold text-ink">
              {current.name}
            </h2>
            <p className="mt-0.5 text-sm text-text-muted">
              {current.whereItShows}
            </p>
            <p className="mt-1.5 text-sm text-ink">
              <strong className="font-semibold">How it changes:</strong>{" "}
              {current.howItChanges}
            </p>
            <p className="mt-1 text-sm text-ink">
              <strong className="font-semibold">Image size:</strong>{" "}
              {current.imageSize}. Images that don&rsquo;t fit get cropped to
              this size.
            </p>
          </div>
        </div>

        {GROUND_OVERLAYS.has(current.key) && (
          <div className="rounded-xl border border-good/30 bg-good-soft px-3.5 py-3 text-sm text-ink">
            <strong className="font-semibold">
              Make it look painted on the grass.
            </strong>{" "}
            Use a PNG with a transparent background, about 60% opacity.
          </div>
        )}

        {children}

        {current.hasTransitions && (
          <SetupSection
            title={
              <>
                Timing{" "}
                <span className="ml-1 text-sm font-normal text-text-muted">
                  (optional)
                </span>
              </>
            }
          >
            <div className="flex flex-wrap gap-3">
              <TimingField
                id="setup-fade-in"
                label="Fade in (sec)"
                value={timing.fadeIn}
                onChange={(fadeIn) => onTimingChange({ ...timing, fadeIn })}
              />
              <TimingField
                id="setup-display"
                label="On screen (sec)"
                value={timing.display}
                onChange={(display) => onTimingChange({ ...timing, display })}
              />
              <TimingField
                id="setup-fade-out"
                label="Fade out (sec)"
                value={timing.fadeOut}
                onChange={(fadeOut) => onTimingChange({ ...timing, fadeOut })}
              />
            </div>
            <p className="text-xs text-text-muted">
              Starts at aG&rsquo;s recommended timing. Change it only if the ad
              feels rushed.
            </p>
          </SetupSection>
        )}
      </div>
    </>
  );
}
